import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Instagram, Music2, Mail, Loader2 } from "lucide-react";
import { brand } from "../mock";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", intention: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Almost there", description: "Please share your name and email so I can write back." });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, {
        name: form.name,
        email: form.email,
        service: form.intention,
        message: form.message,
        source: "contact",
      });
      toast({
        title: "Your message is received",
        description: `Thank you, ${form.name.split(" ")[0]}. Devona will respond within two business days.`,
      });
      setForm({ name: "", email: "", intention: "", message: "" });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: `Please try again in a moment, or email ${brand.email} directly.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 md:py-36 overflow-hidden">
      <div className="cosmic-bg absolute inset-0" />
      <div className="stars stars-dense" />
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full border border-[#D4A85B]/10 animate-orbit-slow" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-14">
        <div className="md:col-span-5">
          <div className="divider-star"><span>Reach out</span></div>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] mt-6 text-[#EDE3D0]">
            If your soul is
            <span className="italic text-[#D4A85B]"> whispering, let it </span>
            speak here first.
          </h2>
          <p className="mt-6 text-[#EDE3D0]/75 text-lg leading-relaxed max-w-md">
            Send a note about where you are and what you're seeking. I read every one personally
            and respond within two business days.
          </p>
          <div className="mt-10 space-y-3 text-sm">
            <a href={`mailto:${brand.email}`} className="flex items-center gap-3 text-[#EDE3D0] hover:text-[#D4A85B] transition">
              <Mail size={16} /> {brand.email}
            </a>
            <a href={brand.instagram} className="flex items-center gap-3 text-[#EDE3D0] hover:text-[#D4A85B] transition">
              <Instagram size={16} /> @healing_innergy
            </a>
            <a href={brand.tiktok} className="flex items-center gap-3 text-[#EDE3D0] hover:text-[#D4A85B] transition">
              <Music2 size={16} /> @healing_inergy
            </a>
          </div>
        </div>

        <form onSubmit={submit} className="md:col-span-7 rounded-3xl p-8 md:p-10 border border-[#D4A85B]/30 bg-[#12081E]/80 backdrop-blur">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/85">Your name</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                type="text"
                className="mt-2 w-full bg-transparent border-b border-[#D4A85B]/30 focus:border-[#D4A85B] outline-none py-2.5 text-[#EDE3D0] placeholder-[#EDE3D0]/40"
                placeholder="Devona"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/85">Email</label>
              <input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                type="email"
                className="mt-2 w-full bg-transparent border-b border-[#D4A85B]/30 focus:border-[#D4A85B] outline-none py-2.5 text-[#EDE3D0] placeholder-[#EDE3D0]/40"
                placeholder="you@email.com"
              />
            </div>
          </div>
          <div className="mt-6">
            <label className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/85">I am drawn to</label>
            <select
              value={form.intention}
              onChange={(e) => setForm({ ...form, intention: e.target.value })}
              className="mt-2 w-full bg-transparent border-b border-[#D4A85B]/30 focus:border-[#D4A85B] outline-none py-2.5 text-[#EDE3D0]"
            >
              <option value="" className="bg-[#12081E]">Choose an offering (optional)</option>
              <option value="Healing Journey Session" className="bg-[#12081E]">Healing Journey Session · $75</option>
              <option value="Transformational Coaching" className="bg-[#12081E]">Transformational Coaching · $75</option>
              <option value="Spiritual Alignment Session" className="bg-[#12081E]">Spiritual Alignment Session · $125</option>
              <option value="Nervous System Reset Intensive" className="bg-[#12081E]">Nervous System Reset Intensive · $200</option>
              <option value="Reiki Session" className="bg-[#12081E]">Reiki Session · $70</option>
              <option value="Not sure yet" className="bg-[#12081E]">I'm not sure yet</option>
            </select>
          </div>
          <div className="mt-6">
            <label className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/85">What's on your heart?</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={5}
              className="mt-2 w-full bg-transparent border-b border-[#D4A85B]/30 focus:border-[#D4A85B] outline-none py-2.5 text-[#EDE3D0] placeholder-[#EDE3D0]/40 resize-none"
              placeholder="Tell me where you are, what you're carrying, and what you're hoping for."
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit-button"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#D4A85B] text-[#0A0714] px-7 py-3.5 text-xs uppercase tracking-[0.28em] hover:bg-[#E9C77B] transition disabled:opacity-70"
          >
            {submitting ? (<><Loader2 size={14} className="animate-spin" /> Sending</>) : (<>Send your note <ArrowRight size={14} /></>)}
          </button>
        </form>
      </div>
    </section>
  );
};

export default Contact;
